import './styles.scss'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { useAppSelector } from '@/store/hook'
import { selectIsAvoidProceedingCart } from '@/store/selectors/cartSelector'
import { selectHideVoucher } from '@/store/selectors/configSelector'
import usePermission from '@/hooks/usePermission'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import {
  MODULE_CART_CHECKOUT_RIGHTS,
  MODULE_SHOP_NAVIGATION_RIGHTS,
} from '@/libs/constants/modulerights'
import { linkGenerator } from '@/libs/utils/language'

import RedeemVoucher from '../RedeemVoucher'
import CartListMobile from './CartListMobile'
import CartSummaryMobile from './CartSummaryMobile'

interface CartMobileProps {
  showAddQuickEntry?: () => void
  isAddBtnDisabled?: boolean
  isLoading?: boolean
  proceedToCheckoutHandler?: () => void
  isShowQuickEntry?: boolean
  shouldTransferCartToProcurement?: boolean
  isPreparingCXMLData?: boolean
}

const CartMobile = ({
  showAddQuickEntry,
  isAddBtnDisabled: isAddButtonDisabled = false,
  proceedToCheckoutHandler,
  isLoading = false,
  isShowQuickEntry = false,
  shouldTransferCartToProcurement = false,
  isPreparingCXMLData = false,
}: CartMobileProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isAvoidProceedingCart = useAppSelector(selectIsAvoidProceedingCart)
  const hideVoucher = useAppSelector(selectHideVoucher)
  const { hasPermission: hasViewIndividualPricePermission } = usePermission(
    MODULE_CART_CHECKOUT_RIGHTS.VIEW_INDIVIDUAL_PRICE,
  )

  return (
    <div className="container-full-width-page">
      <div className="cart__wrapper">
        <div className="full-width-page-component__wrapper cart__heading">
          <h1>{t('cart.header')}</h1>
        </div>
        <CartListMobile isLoading={isLoading} />

        {/* quick entry section */}
        {isShowQuickEntry && (
          <PermissionWrapper
            permission={MODULE_SHOP_NAVIGATION_RIGHTS.ADD_PRODUCT_WITH_QUICK_ENTRY}
          >
            <div className="cart__add-button">
              <CustomButton
                block
                className="cart-heading__add"
                disabled={isAddButtonDisabled || isAvoidProceedingCart}
                type="ghost"
                onClick={showAddQuickEntry}
              >
                {t('cart.addQuickEntryBtn')}
              </CustomButton>
            </div>
          </PermissionWrapper>
        )}
        {hasViewIndividualPricePermission && <CartSummaryMobile isLoading={isLoading} />}

        {/* proceed to checkout page button */}
        {isLoading ? null : (
          <div style={{ marginTop: 30 }}>
            <CustomButton
              block
              disabled={isAvoidProceedingCart || isPreparingCXMLData}
              loading={isPreparingCXMLData}
              onClick={proceedToCheckoutHandler}
            >
              {shouldTransferCartToProcurement
                ? t('cart.proceedToPurchaseSystem')
                : t('cart.proceedToCheckout')}
            </CustomButton>
          </div>
        )}

        <div
          className="cart-bottom__action__link"
          style={{ marginTop: 30, width: '100%' }}
        >
          <CustomButton
            block
            className="cart-bottom__action__link--with-icon"
            type="ghost"
            onClick={async () => navigate(linkGenerator('/my-workspace'))}
          >
            <ChevronLeftIcon />
            {t('cart.continueShopping')}
          </CustomButton>
        </div>
      </div>
      {!hideVoucher && (
        <PermissionWrapper
          permission={MODULE_CART_CHECKOUT_RIGHTS.ADD_VOUCHER_TO_CART}
        >
          <RedeemVoucher isDisabled={isAvoidProceedingCart} />
        </PermissionWrapper>
      )}
    </div>
  )
}

export default CartMobile
